import { useEffect, useState } from "react";
import { CalendarDays, CheckCircle2, Clock, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { getInstallments } from "@/lib/api";
import { formatTRY, formatDate } from "@/lib/constants";

const statusMeta = {
  odendi: { label: "Ödendi", cls: "bg-emerald-700 text-white", Icon: CheckCircle2 },
  bekleyen: { label: "Bekleyen", cls: "bg-slate-700 text-slate-100", Icon: Clock },
  yakin_vade: { label: "Yakın Vade", cls: "bg-amber-700 text-white", Icon: AlertTriangle },
  gecikmis: { label: "Gecikmiş", cls: "bg-red-700 text-white", Icon: AlertTriangle },
};

export default function InstallmentPlan({ caseId, refreshKey }) {
  const [plan, setPlan] = useState([]);

  useEffect(() => {
    getInstallments(caseId).then(setPlan).catch(() => {});
  }, [caseId, refreshKey]);

  if (!plan.length) return null;
  if (plan.length === 1) {
    // Peşin — single installment
    const inst = plan[0];
    const meta = statusMeta[inst.status];
    const I = meta.Icon;
    return (
      <div className="border border-slate-800 rounded px-4 py-3 bg-slate-950/40 flex items-center gap-3" data-testid="installment-plan">
        <CalendarDays className="w-4 h-4 text-amber-500" />
        <div className="text-sm text-slate-200 flex-1">
          Peşin ödeme: <span className="font-mono text-amber-500">{formatTRY(inst.amount)}</span> — Vade: {formatDate(inst.due_date)}
        </div>
        <Badge className={meta.cls}><I className="w-3 h-3 mr-1" />{meta.label}</Badge>
      </div>
    );
  }

  return (
    <div className="border border-slate-800 rounded overflow-hidden" data-testid="installment-plan">
      <div className="px-4 py-2 bg-slate-900/80 flex items-center gap-2 border-b border-slate-800">
        <CalendarDays className="w-4 h-4 text-amber-500" />
        <div className="text-sm text-slate-200 font-medium">Taksit Planı ({plan.length} taksit)</div>
      </div>
      <table className="w-full text-sm">
        <thead className="text-xs text-slate-400 uppercase tracking-wider bg-slate-950/40">
          <tr>
            <th className="px-4 py-2 text-left">#</th>
            <th className="px-4 py-2 text-left">Vade</th>
            <th className="px-4 py-2 text-right">Tutar</th>
            <th className="px-4 py-2 text-right">Durum</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {plan.map((inst) => {
            const meta = statusMeta[inst.status];
            const I = meta.Icon;
            return (
              <tr key={inst.index} className="hover:bg-slate-900/30" data-testid={`installment-row-${inst.index}`}>
                <td className="px-4 py-2 font-mono text-slate-400">{inst.index}</td>
                <td className="px-4 py-2 font-mono text-slate-300">
                  {formatDate(inst.due_date)}
                  {inst.status !== "odendi" && (
                    <span className="text-[10px] text-slate-500 ml-2">
                      {inst.days_remaining < 0 ? `${Math.abs(inst.days_remaining)} gün gecikmiş` :
                       inst.days_remaining === 0 ? "bugün" :
                       `${inst.days_remaining} gün kaldı`}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-right font-mono text-slate-200">{formatTRY(inst.amount)}</td>
                <td className="px-4 py-2 text-right">
                  <Badge className={meta.cls}><I className="w-3 h-3 mr-1" />{meta.label}</Badge>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
